import React, { useEffect, useLayoutEffect, useState } from 'react';
import {
  Box,
  Button,
  MenuItem,
  Tabs,
  Tab,
  Typography,
  Select,
  Grid,
  Checkbox,
  FormControlLabel,
  IconButton,
  Popover,
  List,
  ListItem,
  ListItemText,
  Tooltip,

} from '@mui/material';
import { EditOutlined, TranslateOutlined, VisibilityOutlined, ListAlt } from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { DependencyOperator, FbeDependency, FbeForm, FbeFrontline, FbeLoop, FbeQuestion, FbeQuestionFeature, FbeReferenceType, FbeSection, FbeSectionFeature, fbeSectionFields, FbeType, getLanguageDisplayName, IesMergeMetaData, LocalisedText, LogicalOperator } from '../../services/fbeTypes';
import {
  deepEqual,
  getMergeMetaData,
  getNextQuestionSetId,
  getParentSetId,
  getTag,
  hasSectionChanges,
  isSectionValid,
} from '../../services/service';
import FormPreview from '../FormPreview';
import SectionTranslationsTab from '../SectionTranslationsTab';
import InputField from '../InputField';
import FBCheckbox from '../FBCheckbox';
import InputRowContainer from '../InputRowContainer';
import SingleSelect from '../SingleSelect';
import { activeWorkflowStatusOptions, dislayTypeOptions, generateExternalDataToOptions, generatInternalVariableToOptions, logicalOperatorOptions, operatorOptions, roleOptions, sectionFeatureOptions, sectionWorkflowTaskOptions } from '../../services/options';
import MultiSelect from '../MultiSelect';
import FBRadio from '../FBRadio';
import LineAction, { LineActionType } from '../LineAction';
import EditContainer from '../EditContainer';
import { SmallTabBoxStyleStyled, SmallTabStyleStyled } from '../EditForm';
import { ReferenceImage } from '../ReferenceImage';
import { useImageContext } from '../../imageContext';
import FieldLock from '../FieldLock';
import RichTextField from '../RichTextField';

interface EditSectionFormProps {
  section: FbeSection;
  originalSection?: FbeSection;
  form?: FbeForm;
  allForms?: FbeForm[];
  formSections: FbeSection[];
  sectionNumber: number;
  readOnly: boolean;
  translationsReadOnly?: boolean;
  parentForm?: FbeForm;
  parentSections?: FbeSection[];
  onUpdateSection: (updatedSection: FbeSection) => void;
  onSubmitAllSections: (sectionsOverride?: FbeSection[]) => Promise<void>;
  language: string;
  supportedLanguages: string[];
  frontline?: FbeFrontline;
  imageList: string[];
  initialTab?: number;
}

const normalizeSectionWorkflowTask = (section?: FbeSection): FbeSection | undefined => {
  if (!section) {
    return undefined;
  }

  const normalizedSection = JSON.parse(JSON.stringify(section)) as FbeSection & {
    workflowTaskType?: string;
  };

  if (!normalizedSection.workflowTask) {
    normalizedSection.workflowTask = normalizedSection.workflowTaskType;
  }

  delete normalizedSection.workflowTaskType;

  return normalizedSection;
};

export default function EditSection(props: EditSectionFormProps): JSX.Element {
  const defaultFbeFormSection: FbeSection = {
    active: true,
    order: 0,
    name: [],
    questions: [],
    tasks: [],
    loop: {
      name: [],
      ascending: true,
      total: 0,
      externalVariable: '',
      ascendingInternalVariable: '',
      ascendingInternalValue: '',
    },
    groupHeader: false,
    mergeMetaData: {
      questionSetId: props.form
        ? getNextQuestionSetId(props?.form, props.sectionNumber)
        : '',
      parentSetId: props.form ? getParentSetId(props.form) : '',
      tacoRole: props.form ? props.form.role : [],
      description: props.section
        ? props.section.name
        : [{ language: props.language, text: '' }],
      tag: props.form && props.section ? getTag(props.form, props.section) : '',
    },
  };
  const [tabIndex, setTabIndex] = useState(props.initialTab ?? 0);
  const [smallSectionTabIndex, setSmallSectionTabIndex] = useState<number>(0);
  const [previewLanguage, setPreviewLanguage] = useState<string>(props.language);
  const [parentAnchorEl, setParentAnchorEl] = useState<HTMLElement | null>(null);
  const history = useNavigate();
  const [formSectionState, setFormSectionState] = useState<FbeSection>(
    defaultFbeFormSection
  );
  const [touchedFields, setTouchedFields] = useState<{
    [key: string]: boolean;
  }>({});
  const [isLoopingEnabled, setIsLoopingEnabled] = useState<boolean>(false);
  const [manualUpdateFlag, setManualUpdateFlag] = useState(false);
  const [formSectionValid, setFormSectionValid] = useState(false);
  const [changesMade, setChangesMade] = useState(false);
  const [groupHeaderError, setGroupHeaderError] = useState<string>('');

  const {
    setQuestionImage,
    setSelected,
    setTimestamp,
    setCurrentElement,
    setItemList
  } = useImageContext();

  useEffect(() => {
    setItemList(props.imageList);
  }, [props.imageList, setItemList]);

  useEffect(() => {
    setPreviewLanguage(props.language);
  }, [props.language]);

  useLayoutEffect(() => {
    const currentImage: string = (props.section.references &&
    props.section.references[0] &&  props.section.references[0].location) ?
    props.section.references[0].location : '';

    setSelected(currentImage);
    setQuestionImage(currentImage);
    setTimestamp('');
    setCurrentElement(props.section.sk || 'section')
  }, []);

  useEffect(() => {
    // Only reset state from props on initial mount or when a new section is loaded
    if (!manualUpdateFlag) {
      setFormSectionState(normalizeSectionWorkflowTask(props.section) || ({} as FbeSection));
    }
    // manualUpdateFlag is set to true after first manual change
  }, [props.section]);

  useEffect(() => {
    const invalidSections = props.formSections.filter(formSection => !isSectionValid(formSection,[]))
    const isValid = isSectionValid(formSectionState,[]) && invalidSections.length === 0

    setFormSectionValid(isValid);

    const originalFormSections = normalizeSectionWorkflowTask(props.originalSection);

    if (formSectionState?.description === '')
      if (originalFormSections) originalFormSections.description = '';

    // Check if any changes were made
    const isChanged =
      !deepEqual(formSectionState, originalFormSections);
    setChangesMade(isChanged);

    setIsLoopingEnabled( formSectionState.loop !== undefined );
  }, [formSectionState, props.originalSection, props.formSections]);

  const hanldeDefaultMergeMetaData = () => {
    const section = JSON.parse(JSON.stringify(props.section)); // Deep copy

    if(!props.section.mergeMetaData || props.section.mergeMetaData?.tag === '' || props.section.mergeMetaData?.tag.includes('new_section')){
        const mergeMetaData = getMergeMetaData(props.form || {} as FbeForm, section, props.sectionNumber)
        section['mergeMetaData'] = mergeMetaData;
      }

    props.onUpdateSection(section); // Notify the parent
  }

  const addLoop = () => {
    formSectionState.loop = {} as FbeLoop;

    const updatedSection = JSON.parse(JSON.stringify(formSectionState)) as FbeSection;
    updatedSection.loop = {} as FbeLoop;
    props.onUpdateSection(updatedSection); // Notify the parent
  }

  const removeLoop = () => {
    const updatedSection = JSON.parse(JSON.stringify(formSectionState)) as FbeSection;
    delete updatedSection.loop;
    setFormSectionState(updatedSection);
    setManualUpdateFlag(true); // Mark that user has made a manual change
    props.onUpdateSection(updatedSection); // Notify the parent
  }

  const handleSectionChange = <K extends keyof FbeSection>(
    field: K,
    value: FbeSection[K] | string | string[],
    language = props.language,
    dependencyField?: string,
    index?: number,
  ) => {
    const section = JSON.parse(JSON.stringify(props.section)); // Deep copy
    const updatedSection = { ...section };

    if (field === 'name' && typeof value === 'string') {
      const updatedName = section.name;
      if(updatedName.length === 0){
        updatedName.push({
          language,
          text: value as string
        })
      } else{
        updatedName[0].text = value as string;
      }

      updatedSection[field] = updatedName as FbeSection[K];
    } else if (field === 'description'){
      if(!value || value === ''){
        delete updatedSection.description;
      } else{
        updatedSection[field] = value as FbeSection[K];
      }
    } else if (field === 'features'){
      if(!value || (value as string[]).length === 0){
        delete updatedSection.features;
      } else{
        updatedSection[field] = value as FbeSection[K];
      }
      // Cascade features from section to all questions (even when all features removed)
      const newFeatures = (value as string[]) || [];
      const oldFeatures = section.features || [];

      // Get original section for comparison
      const originalSection = props.originalSection;

      // Detect which features were added
      const reorderingAdded = newFeatures.includes(FbeSectionFeature.ALLOW_REORDERING_BY_SPV) &&
                             !oldFeatures.includes(FbeSectionFeature.ALLOW_REORDERING_BY_SPV);
      const removalsAdded = newFeatures.includes(FbeSectionFeature.ALLOW_REMOVALS_BY_SPV) &&
                           !oldFeatures.includes(FbeSectionFeature.ALLOW_REMOVALS_BY_SPV);

      // Detect which features were removed
      const reorderingRemoved = !newFeatures.includes(FbeSectionFeature.ALLOW_REORDERING_BY_SPV) &&
                               oldFeatures.includes(FbeSectionFeature.ALLOW_REORDERING_BY_SPV);
      const removalsRemoved = !newFeatures.includes(FbeSectionFeature.ALLOW_REMOVALS_BY_SPV) &&
                             oldFeatures.includes(FbeSectionFeature.ALLOW_REMOVALS_BY_SPV);

      // Handle additions: Add all newly-added features in one pass
      if ((reorderingAdded || removalsAdded) && updatedSection.questions && updatedSection.questions.length > 0) {
        updatedSection.questions = updatedSection.questions.map((question: FbeQuestion) => {
          let questionFeatures = question.features || [];
          let questionModified = false;

          // Add ALLOW_REORDERING_BY_SPV if it was just added at section level
          if (reorderingAdded && !questionFeatures.includes(FbeQuestionFeature.ALLOW_REORDERING_BY_SPV)) {
            questionFeatures = [...questionFeatures, FbeQuestionFeature.ALLOW_REORDERING_BY_SPV];
            questionModified = true;
          }

          // Add ALLOW_REMOVALS_BY_SPV if it was just added at section level
          if (removalsAdded && !questionFeatures.includes(FbeQuestionFeature.ALLOW_REMOVALS_BY_SPV)) {
            questionFeatures = [...questionFeatures, FbeQuestionFeature.ALLOW_REMOVALS_BY_SPV];
            questionModified = true;
          }

          if (!questionModified) {
            return question;
          }

          return {
            ...question,
            features: questionFeatures
          };
        });
      }

      // Handle removals: Remove all removed features in one pass
      if ((reorderingRemoved || removalsRemoved) && updatedSection.questions && updatedSection.questions.length > 0) {
        updatedSection.questions = updatedSection.questions.map((question: FbeQuestion, questionIndex: number) => {
          const originalQuestion = originalSection?.questions?.[questionIndex];
          let questionFeatures = question.features || [];
          let questionModified = false;

          // Remove ALLOW_REORDERING_BY_SPV if it was just removed at section level
          if (reorderingRemoved && questionFeatures.includes(FbeQuestionFeature.ALLOW_REORDERING_BY_SPV)) {
            questionFeatures = questionFeatures.filter(f => f !== FbeQuestionFeature.ALLOW_REORDERING_BY_SPV);
            questionModified = true;
          }

          // Remove ALLOW_REMOVALS_BY_SPV if it was just removed at section level
          if (removalsRemoved && questionFeatures.includes(FbeQuestionFeature.ALLOW_REMOVALS_BY_SPV)) {
            questionFeatures = questionFeatures.filter(f => f !== FbeQuestionFeature.ALLOW_REMOVALS_BY_SPV);
            questionModified = true;
          }

          if (!questionModified) {
            return question;
          }

          // Check if question features match original after removal
          const finalQuestionFeatures = questionFeatures.length > 0 ? questionFeatures : undefined;
          const originalQuestionFeatures = originalQuestion?.features;

          // If features match original, return the original question to preserve reference
          if (originalQuestion && JSON.stringify(finalQuestionFeatures) === JSON.stringify(originalQuestionFeatures)) {
            return originalQuestion;
          }

          return {
            ...question,
            features: finalQuestionFeatures
          };
        });
      }
    } else if (field === 'workflowDependency'){
      if(!value || (value as string[]).length === 0){
        delete updatedSection.workflowDependency;
      } else{
        updatedSection[field] = value as FbeSection[K];
      }
    } else if (field === 'groupId') {
      if (!value || value === '') {
        updatedSection.groupHeader = false;
        delete updatedSection.groupId;
      } else {
        updatedSection[field] = value as FbeSection[K];
      }
      setGroupHeaderError('');
        } else if (field === 'groupHeader') {
          // Only allow one header per groupId
          const currentGroupId = formSectionState.groupId;
          if (value === true && currentGroupId) {
            const otherHeaders = props.formSections.filter(
              (section) =>
                section.groupId === currentGroupId &&
                section.groupHeader === true &&
                section !== props.section
            );
            if (otherHeaders.length > 0) {
              setGroupHeaderError('Only one Group Header allowed per group.');
              return;
            }
          }
          updatedSection.groupHeader = !!value;
          setGroupHeaderError('');
    } else if (
      field === 'mergeMetaData' &&
      typeof value === 'object' &&
      value !== null
    ) {
      const mergeMetaDataValue = value as typeof section['mergeMetaData'] as IesMergeMetaData;
      updatedSection[field] = {
        ...mergeMetaDataValue,
      } as FbeSection[K];

    } else if(field === 'internalDependency'){
      const internalDependency = updatedSection.internalDependency as FbeDependency[] || [{
        logicalOperator: LogicalOperator.AND,
        variable: '',
        operator: DependencyOperator.EQUALS,
        compareVariable: '',
        compareValue: ''
      }] as FbeDependency[];

      const dependencyIndex = index || 0;

      switch(dependencyField){
        case 'logicalOperator':
          internalDependency[dependencyIndex].logicalOperator = value as LogicalOperator;
          break
        case 'variable':
          internalDependency[dependencyIndex].variable = value as string;
          break
        case 'operator':
          internalDependency[dependencyIndex].operator = value as DependencyOperator;
          break
        case 'compareVariable':
          internalDependency[dependencyIndex].compareVariable = value as string;
          break
        case 'compareValue':
          internalDependency[dependencyIndex].compareValue = value as string;
          break
        default:
          break;
      }

      updatedSection[field] = internalDependency
    } else if(field === 'externalDependency'){
      const externalDependency = updatedSection.externalDependency as FbeDependency[] || [{
        logicalOperator: LogicalOperator.AND,
        variable: '',
        operator: DependencyOperator.EQUALS,
        compareVariable: '',
        compareValue: ''
      }] as FbeDependency[];

      const dependencyIndex = index || 0;

      switch(dependencyField){
        case 'logicalOperator':
          externalDependency[dependencyIndex].logicalOperator = value as LogicalOperator;
          break
        case 'variable':
          externalDependency[dependencyIndex].variable = value as string;
          break
        case 'operator':
          externalDependency[dependencyIndex].operator = value as DependencyOperator;
          break
        case 'compareVariable':
          externalDependency[dependencyIndex].compareVariable = value as string;
          break
        case 'compareValue':
          externalDependency[dependencyIndex].compareValue = value as string;
          break
        default:
          break;
      }

      updatedSection[field] = externalDependency
    } else {
      updatedSection[field] = value as FbeSection[K];
    }

    if (field === 'workflowTask') {
      delete (updatedSection as FbeSection & { workflowTaskType?: string }).workflowTaskType;
    }

    props.onUpdateSection(updatedSection); // Notify the parent
  };

  const handleTabChange = (_: React.ChangeEvent<{}>, newIndex: number) => {
    setTabIndex(newIndex);
  };

  const handleSmallTabChange = (_: React.ChangeEvent<{}>, newIndex: number) => {
    setSmallSectionTabIndex(newIndex);
  };

  const getLoopCharacteristics = (): string[] => {
    if (formSectionState.loop?.externalVariables) {
      return formSectionState.loop.externalVariables;
    }

    return [];
  };

  const updateLoop = (changes: Partial<FbeLoop>) => {
    handleSectionChange('loop', {
      ...formSectionState.loop,
      ascending: formSectionState.loop?.ascending === true,
      name: formSectionState.loop?.name ?? [{ language: props.language, text: '' }],
      total: formSectionState.loop?.total ?? 0,
      externalVariable: formSectionState.loop?.externalVariable || '',
      externalVariables: formSectionState.loop?.externalVariables,
      ascendingInternalVariable: formSectionState.loop?.ascendingInternalVariable || '',
      ascendingInternalValue: formSectionState.loop?.ascendingInternalValue || '',
      ...changes,
    });
  };

  const handleExternalVariableChange = (values: string[]) => {
    updateLoop({ externalVariable: values.join(';') });
  };

  const handleLoopCharacteristicChange = (index: number, value: string) => {
    const characteristics = [...getLoopCharacteristics()];
    if (
      value &&
      characteristics.some(
        (characteristic, characteristicIndex) =>
          characteristicIndex !== index && characteristic === value
      )
    ) {
      return;
    }
    characteristics[index] = value;
    const selectedCharacteristics = characteristics.filter((characteristic) => characteristic !== '');

    if (selectedCharacteristics.length > 0) {
      updateLoop({ externalVariables: selectedCharacteristics });
    } else {
      updateLoop({ externalVariables: undefined });
    }
  };

  const addLoopCharacteristic = () => {
    updateLoop({ externalVariables: [...getLoopCharacteristics(), ''] });
  };

  const removeLoopCharacteristic = (index: number) => {
    const characteristics = [...getLoopCharacteristics()];
    characteristics.splice(index, 1);
    updateLoop({
      externalVariables:
        characteristics.filter((characteristic) => characteristic !== '').length > 0
          ? characteristics
          : undefined,
    });
  };

  const handleAddDependency = (
    external: boolean,
  ) => {
    const section = JSON.parse(JSON.stringify(props.section)); // Deep copy
    const updatedSection = { ...section } as FbeSection;

    if(external){
      if(!updatedSection.externalDependency){
        updatedSection.externalDependency = [];
      }
      updatedSection.externalDependency.push({
        logicalOperator: LogicalOperator.AND,
        variable: '',
        operator: DependencyOperator.EQUALS
      });
    } else if(!external ){
      if(!updatedSection.internalDependency){
        updatedSection.internalDependency = [];
      }
      updatedSection.internalDependency.push({
        logicalOperator: LogicalOperator.AND,
        variable: '',
        operator: DependencyOperator.EQUALS
      });
    }

    props.onUpdateSection(updatedSection); // Notify the parent

  }

  const handleRemoveDependency = (
    external: boolean,
    externalIndex: number
  ) => {
    const section = JSON.parse(JSON.stringify(props.section)); // Deep copy
    const updatedSection = { ...section } as FbeSection;

    if(external && (updatedSection.externalDependency || []).length > externalIndex){
      updatedSection.externalDependency?.splice(externalIndex,1);
    } else if(!external && (updatedSection.internalDependency || []).length > externalIndex){
      updatedSection.internalDependency?.splice(externalIndex,1);
    }

    if(updatedSection.externalDependency && updatedSection.externalDependency.length === 0){
      delete updatedSection.externalDependency;
    }

    if(updatedSection.internalDependency && updatedSection.internalDependency.length === 0){
      delete updatedSection.internalDependency;
    }

    props.onUpdateSection(updatedSection); // Notify the parent
  };

  const getMergeMetaDataDescription = (
    language: string,
    value: string,
    description?: LocalisedText[]): LocalisedText[] => {
    if(!description || description.length === 0){
      return [{
        language,
        text: value
      }];
    }

    const updatedDescription = JSON.parse(JSON.stringify(description)) as LocalisedText[];

    const found = updatedDescription.findIndex(element => element.language === language);

    if(found < 0){
      updatedDescription.push({
        language,
        text: value
      })
    } else {
      updatedDescription[found].text = value;
    }

    return updatedDescription;
  }

  const handleOpenParentSetList = (e: any) => {
    setParentAnchorEl(e.currentTarget);
  };

  const handleCloseParentSetList = () => {
    setParentAnchorEl(null);
  };

  const giDescriptionHelpTitle='Description (optional)';
  const giDescriptionHelpBody='Add a description to explain what the section is about and and important information such that other administrators understand the purpose of this section.';
  const giTitleHelpTitle='Section title (mandatory)';
  const giTitleHelpBody='Give an easy, short readable name to the section that encompasses the essence of it.';
  const giActiveTitle='Active (mandatory)';
  const giActiveBody='When checked the section is used when applicable. <><>Leave it unchecked if you don\'t want it to be used yet or keep it as archive.';
  const giOrderHelpTitle='Order (mandatory)';
  const giOrderHelpBody='This field displays the order of the section. <><>Drag the sections at the left menu bar to change the order.';
  const dDependencyWorkflowTitle='Dependency workflow (optional)';
  const dDependencyWorkflowBody='Only displays this section in the viewer when the workflow state is active';

  const dDisplayHelpTitle='Display type (optional)';
  const dDisplayHelpBody='Set the way the questions in the section are displayed.<><>List when the questions are displayed from top to bottom.<><>Single when the questions are displayed one at a time.<><>Table for a table structure (numbers).';
  const dGroupIdHelpTitle='Group identifier (optional)';
  const dGroupIdHelpBody='Use a group identifier to connect multiple sections together. <><>The displaying application may (or may not) take advantage of this.';

  const mdRolesTitle = 'Role(s) (mandatory)';
  const mdRolesBody = 'Select the role(s) who should fill in this section. <><>The values are pre populated by the default roles defined in the form. <><>Only adjust here when needed.';
  const mdTagHelpTitle = 'Tag (mandatory)';
  const mdTagHelpBody = 'Tag is used for internal identification.<><>Keep the proposed value unless you specifically need to change it.';
  const mdDescriptionHelpTitle='Navigation text (optional)';
  const mdDescriptionHelpBody='Add a text to display in section navigation.';
  const mdQuestionSetIDHelpTitle = 'Question set ID (mandatory)';
  const mdQuestionSetIDHelpBody = 'Question set ID is inherited from TACO. <><>The inital value should be sufficient. <><>If needed it can be changed to any value. <><>Mind the risk that forms with the same value will overwrite data in the viewing application.';
  const mdParentSetIDHelpTitle = 'Parent set ID (mandatory)';
  const mdParentSetIDHelpBody = 'Parent set ID is inherited from TACO. This ID specifies the parent for all question SET ID(s). <><>The inital value should be sufficient. <><>If needed it can be changed to any value. <><>Mind the risk that forms with the same value will overwrite data in the viewing application.';

  const lLoopingHelpTitle = 'Looping (mandatory)';
  const lLoopingHelpBody = 'Enable looping to display the section  questions a number of times, depending on a variable or static total. ';
  const lNameHelpTitle = 'Looping (mandatory)';
  const lNameHelpBody = 'Give an easy, short readable name to the loop that encompasses the essence of it. This value might be displayed, postfixed with a number, on the displaying application.';
  const lTotalHelpTitle = 'Total (mandatory)';
  const lTotalHelpBody = 'Provide a static value for the total loop times. <><>When using a variable this value will become the default fallback total.';
  const lVariableHelpTitle = 'Variable (optional)';
  const lVariableHelpBody = 'Select one or two variable(s) from the mapping table from which the number value(s) are used as the looping total.';
  const lOrderHelpTitle = 'Order (mandatory)';
  const lOrderHelpBody = 'Select the looping order.<><>Ascending is from 1 and up.<><>Descending is from the maximum value down.';
  const lInternalVariableOrderHelpTitle = 'Loop order internal variable (optional)';
  const lInternalVariableOrderHelpBody = 'Use an internal variable to define the order during run time. It will expect an';
  const lInternalValueOrderHelpTitle = 'Loop order internal value (optional)';
  const lInternalValueOrderHelpBody = 'When the internal variable has this defined value the order will be ascending.';

  const sdLogicalOperatorTitle = 'Logical operator (mandatory)';
  const sdLogicalOperatorBody = 'Select the desired operator.<><>AND if all dependecies must apply.<><>OR if one needs to apply.';
  const sdVariableTitle = 'Variable (mandatory)';
  const sdVariableBody = 'Select the (external/ internal) variable for the dependency.';
  const sdOperatorTitle = 'Operator (mandatory';
  const sdOperatorBody = 'Select the desired operator.<><>Equals, Not equals, Includes, Not includes, Greater or Smaller.';
  const sdCompareValueTitle = 'Compare value (optional)';
  const sdCompareValueBody = 'Compare the selected variable against this value';
  const sdCompareVariableTitle = 'Compare variable (optional)';
  const sdCompareVariableBody = 'Compare the selected variable against this variable.';
  const sdRemoveDependencyTitle = 'Remove dependency';
  const sdRemoveDependencyBody = 'Deletes above dependency.';
  const sdAddDependencyTitle = 'Add dependency';
  const sdAddDependencyBody = 'Add a default dependency. Then update the fields.';

  const dFeatureTitle = 'Feature(s) (optional)';
  const dFeatureBody = 'Select the features(s) for the form (when supported by displaying application).<><>No changes to section fields (extend) = select this option when creating a global form and do not allow addition of local sections.<><>No additional questions (extend) = disable local admins to add local questions.';

  const parentSection = (props.parentSections || []).length > 0 ? (props.parentSections || []).find(section => section.mergeMetaData?.questionSetId === formSectionState.mergeMetaData?.questionSetId) : null;

  const isInitial = formSectionState.name.length === 0;

  const validGeneralInfo = isInitial || isSectionValid(formSectionState,['name','active']);
  const validDetail = isInitial || isSectionValid(formSectionState,['displayType']);
  const validDependency = isInitial || isSectionValid(formSectionState,['externalDependency','internalDependency']);
  const validMetaData = isInitial || isSectionValid(formSectionState,['mergeMetaData']);
  const validLoop = isInitial || isSectionValid(formSectionState,['loop']);
  const validReferences = isSectionValid(formSectionState,['references']);

  const originalSectionForComparison = normalizeSectionWorkflowTask(props.originalSection) || {} as FbeSection;
  const changedGeneralInfo = !isInitial && hasSectionChanges(originalSectionForComparison, formSectionState,['name','active','order','description']);
  const changedDetail = !isInitial && hasSectionChanges(originalSectionForComparison, formSectionState,['features','displayType','groupId','workflowTask']);
  const changedDependency = !isInitial && hasSectionChanges(originalSectionForComparison, formSectionState,['workflowDependency','externalDependency','internalDependency']);
  const changedMetaData = !isInitial && hasSectionChanges(originalSectionForComparison, formSectionState,['mergeMetaData']);
  const changedLoop = !isInitial && hasSectionChanges(originalSectionForComparison, formSectionState,['loop']);
  const changedReferences = hasSectionChanges(originalSectionForComparison, formSectionState,['references']);

  const currentLanguages: string[] = [];
  if(props.form && props.form.name && props.form.name.length > 0){
    props.form.name.map(element => {currentLanguages.push(element.language)})
  }

  const baseLanguage = currentLanguages.length > 0 ? currentLanguages[0] : 'invalid';

  const readOnlySection = props.readOnly;

  const referenceImageList: string[] = []

  formSectionState.references?.map(reference => {
    if(reference.location &&
      (reference.type === FbeReferenceType.IMAGE || reference.type === FbeReferenceType.TIP_IMAGE)
    ){
      referenceImageList.push(reference.location)
    }
  });

  const handleOnAddImage = (image: string): void => {
    const updatedSection = JSON.parse(JSON.stringify(formSectionState)) as FbeSection;
    if(!updatedSection.references){
      updatedSection.references = [];
    }
    updatedSection.references.push({
      type: FbeReferenceType.IMAGE,
      location: image
    });

    setFormSectionState(updatedSection);
    props.onUpdateSection(updatedSection);
  }

  const handleOnRemoveImage = (image: string): void => {
    const updatedSection = JSON.parse(JSON.stringify(formSectionState)) as FbeSection;
    if(!updatedSection.references){
      updatedSection.references = [];
    }
    const updatedReferences = updatedSection.references.filter( reference =>
      reference.location !== image
    );
    updatedSection.references = updatedReferences;

    if(updatedSection.references.length === 0){
      delete updatedSection.references;
    }

    setFormSectionState(updatedSection);
    props.onUpdateSection(updatedSection);
  }

  const handleAllLockedFields = (
    locked: boolean
  ) => {
    const { lockedFields, ...prev} = formSectionState;
    let updatedLockedFields = lockedFields || [];
    if(locked){
      updatedLockedFields = JSON.parse(JSON.stringify(fbeSectionFields)) as string[];
    } else {
      updatedLockedFields = [];
    }

    if(updatedLockedFields.length > 0){
      setFormSectionState({...prev, lockedFields: updatedLockedFields});
    } else {
      setFormSectionState(prev);
    }
  }

  const handleLockedFields = (
    field: string,
    locked: boolean
  ) => {
    const { lockedFields, ...prev} = formSectionState;
    let updatedLockedFields = lockedFields || [];
    if(locked){
      updatedLockedFields.push(field);
    } else {
      updatedLockedFields = updatedLockedFields.filter(updatedLockedField => updatedLockedField !== field);
    }

    updatedLockedFields = Array.from(new Set(updatedLockedFields));

    const updatedSection = updatedLockedFields.length > 0 ? {...prev, lockedFields: updatedLockedFields.sort()} : prev;
    setFormSectionState(updatedSection);
    props.onUpdateSection(updatedSection);
  }

  const locksDisabled =  !formSectionState || (
    props.form?.pk !== 'form_global' && props.form?.type === FbeType.FULL_MERGE);

  const locksDisplayed = formSectionState && (
    props.form?.pk === 'form_global' || props.form?.type === FbeType.FULL_MERGE);

  return (
    <>
        <Grid size={{ xs: 12, sm: 8, md: 9 }}>
          <Box
            display="flex"
            justifyContent="space-between"
            alignItems="center"
            sx={{ backgroundColor: '#f5f5f5', padding: '8px' }}
          >
            <Tabs
              value={tabIndex}
              onChange={handleTabChange}
              aria-label="form tabs"
            >
              <Tab
                label={
                  <Box sx={{ display: 'flex', alignItems: 'center' }}>
                    <EditOutlined sx={{ marginRight: '8px' }} />
                    Edit
                  </Box>
                }
              />
              <Tab
                label={
                  <Box sx={{ display: 'flex', alignItems: 'center' }}>
                    <TranslateOutlined sx={{ marginRight: '8px' }} />
                    Translations
                  </Box>
                }
              />
              <Tab
                label={
                  <Box sx={{ display: 'flex', alignItems: 'center' }}>
                    <VisibilityOutlined sx={{ marginRight: '8px' }} />
                    Preview
                  </Box>
                }
              />
            </Tabs>

            <Box display="flex" alignItems="center">
              <Typography variant="body2" sx={{ marginRight: '8px' }}>
                Localization:
              </Typography>
              <Select
                value={previewLanguage}
                sx={{ minWidth: 120 }}
                onChange={(e) => setPreviewLanguage(e.target.value as string)}
              >
              {
              props.supportedLanguages.length > 0 ? props.supportedLanguages.map(languageOption => (
                  <MenuItem key={languageOption} value={languageOption} selected={props.language === languageOption} data-testid={`form-language-${languageOption}`}>
                    {getLanguageDisplayName(languageOption, props.frontline?.settings.languageDisplayNames || {})} { currentLanguages.includes(languageOption) ? (baseLanguage === languageOption ? '(*)' : '(T)') : ''}
                  </MenuItem>
              )) : (<></>)}
              </Select>
            </Box>
          </Box>

          <Box mt={3} sx={{ paddingLeft: '24px', paddingRight: '24px' }}>
            {tabIndex === 0 && (
              <>
                <Box>
                  <Box
                    display="flex"
                    justifyContent="space-between"
                    alignItems="center"
                  >
                    <Typography variant="h6">
                      <FieldLock
                        onChange={function (locked: boolean): void {
                            handleAllLockedFields(locked);
                        } }
                        labelId={'all'}
                        disabled={locksDisabled}
                        displayed={locksDisplayed}
                        size={24}
                        value={(formSectionState.lockedFields || []).length === fbeSectionFields.length}/>
                      Edit Section</Typography>
                  </Box>
                  <Box mt={2}>
                    <InputField
                      labelText={'Title'}
                      labelId={'name'}
                      mandatory={true}
                      helpTextTitle={giTitleHelpTitle}
                      helpTextBody={giTitleHelpBody}
                     onChange={(value) => {
                      setFormSectionState(prev => ({
                        ...prev,
                        name: [{ language: props.language, text: value }]
                      }));
                       handleSectionChange('name', value);
                    }}
                    onBlur={() => {
                      hanldeDefaultMergeMetaData();
                      setTouchedFields((prev) => ({
                          ...prev,
                          name: true,
                      }));
                    }}
                      fullWidth={true}
                      value={formSectionState.name?.[0]?.text || ''}
                      error={
                        touchedFields.name && !formSectionState.name[0].text
                      }
                      helperText={
                        touchedFields.name && !formSectionState.name[0].text
                          ? 'Title is required'
                          : ''
                      }
                      disabled={readOnlySection || (locksDisabled && (formSectionState.lockedFields || []).includes('name') )}
                      onLock={handleLockedFields}
                      disabledLock={locksDisabled}
                      displayedLock={locksDisplayed}
                      isLocked={(formSectionState.lockedFields || []).includes('name')}
                      />
                      <FBCheckbox
                        checked={formSectionState.active}
                        labelText={'Active'}
                        labelId={'active'}
                        mandatory={true}
                        helpTextTitle={giActiveTitle}
                        helpTextBody={giActiveBody}
                        onChange={(checked) =>
                          handleSectionChange('active', checked)
                        }
                        disabled={readOnlySection || (locksDisabled && (formSectionState.lockedFields || []).includes('active') )}
                        onLock={handleLockedFields}
                        disabledLock={locksDisabled}
                    displayedLock={locksDisplayed}
                        isLocked={(formSectionState.lockedFields || []).includes('active')}
                    />
                  </Box>
                  <SmallTabBoxStyleStyled>
                    <Tabs
                      value={smallSectionTabIndex}
                      onChange={handleSmallTabChange}
                      variant="scrollable"
                      scrollButtons="auto"
                      sx={{ flexGrow: 1, minWidth: 0}}
                    >
                      <Tab
                        label="General Info"
                        sx={{
                          ...( changedGeneralInfo ? { backgroundColor: '#F9E20D' } : {} ),
                          ...( !validGeneralInfo ? { backgroundColor: '#FF2A31' } : {} ),
                          margin:'4px',
                          minWidth: 'auto',
                          width: 'auto',
                          flex: 1,
                          textOverflow: 'ellipsis',
                          overflow: 'hidden',
                          whiteSpace: 'nowrap',
                        }}
                      />
                      <SmallTabStyleStyled
                        label="Details"
                        sx={{
                          ...( changedDetail ? { backgroundColor: '#F9E20D' } : {} ),
                          ...( !validDetail ? { backgroundColor: '#FF2A31' } : {} ),
                          margin:'4px',
                          minWidth: 'auto', width: 'auto', flex: 1 }}
                      />
                      <SmallTabStyleStyled
                        label="Dependency"
                        sx={{
                          ...( changedDependency ? { backgroundColor: '#F9E20D' } : {} ),
                          ...( !validDependency ? { backgroundColor: '#FF2A31' } : {} ),
                          margin:'4px',
                          minWidth: 'auto', width: 'auto', flex: 1 }}
                      />
                      <SmallTabStyleStyled
                        label="Metadata"
                        sx={{
                          ...( changedMetaData ? { backgroundColor: '#F9E20D' } : {} ),
                          ...( !validMetaData ? { backgroundColor: '#FF2A31' } : {} ),
                          margin:'4px',
                          minWidth: 'auto', width: 'auto', flex: 1 }}
                      />
                      <SmallTabStyleStyled
                        label="Loop"
                        sx={{
                          ...( changedLoop ? { backgroundColor: '#F9E20D' } : {} ),
                          ...( !validLoop ? { backgroundColor: '#FF2A31' } : {} ),
                          margin:'4px',
                          minWidth: 'auto', width: 'auto', flex: 1 }}
                      />
                      <SmallTabStyleStyled
                        label="References"
                        sx={{
                          ...( changedReferences ? { backgroundColor: '#F9E20D' } : {} ),
                          ...( !validReferences ? { backgroundColor: '#FF2A31' } : {} ),
                          margin:'4px',
                          minWidth: 'auto', width: 'auto', flex: 1 }}
                      />
                    </Tabs>
                    </SmallTabBoxStyleStyled>
                    <EditContainer>
                  {smallSectionTabIndex === 0 && (
                    <Box>
                      <InputRowContainer>
                        <InputField
                          labelText={'Order'}
                          labelId={'order'}
                          mandatory={true}
                          helpTextTitle={giOrderHelpTitle}
                          helpTextBody={giOrderHelpBody}
                          onChange={(value) =>
                            void value
                          }
                          onBlur={() =>
                            setTouchedFields((prev) => ({
                              ...prev,
                              order: true,
                              }))
                          }
                          fullWidth={false}
                          value={formSectionState.order}
                          error={
                            touchedFields.order &&
                            formSectionState.order < 0
                          }
                          helperText={
                            touchedFields.order &&
                            formSectionState.order < 0
                              ? 'Order is required'
                              : ''
                          }
                          type="number"
                          disabled={true}
                          onLock={handleLockedFields}
                          disabledLock={locksDisabled}
                    displayedLock={locksDisplayed}
                          isLocked={(formSectionState.lockedFields || []).includes('order')}
                        />
                      </InputRowContainer>
                      <InputRowContainer>
                      {/* <InputField
                        labelText={'Description'}
                        labelId={'description'}
                        mandatory={false}
                        helpTextTitle={giDescriptionHelpTitle}
                        helpTextBody={giDescriptionHelpBody}
                        onChange={(value) =>
                          handleSectionChange('description', value)
                        }
                        onBlur={() =>
                          setTouchedFields((prev) => ({
                            ...prev,
                            description: true,
                          }))
                        }
                        fullWidth={true}
                        rows={4}
                        value={formSectionState.description || ''}
                        error={false}
                        helperText={''}
                        disabled={readOnlySection || (locksDisabled && (formSectionState.lockedFields || []).includes('description') )}
                        onLock={handleLockedFields}
                        disabledLock={locksDisabled}
                    displayedLock={locksDisplayed}
                        isLocked={(formSectionState.lockedFields || []).includes('description')}
                      /> */}
                     <RichTextField
                        labelText="Description"
                        labelId="description"
                        value={formSectionState.description || ''}
                        onChange={(html) => {
                          setFormSectionState(prev => ({ ...prev, description: html }));
                        }}
                        onBlur={() => {
                          handleSectionChange('description', formSectionState.description || '');
                        }}
                        helpTextTitle={giDescriptionHelpTitle}
                        helpTextBody={giDescriptionHelpBody}
                        disabled={readOnlySection || (formSectionState.lockedFields || []).includes('description')}
                        onLock={handleLockedFields}
                        disabledLock={locksDisabled}
                        displayedLock={locksDisplayed}
                        locked={(formSectionState.lockedFields || []).includes('description')}
                      />

                    </InputRowContainer>
                  </Box>
                    )}

                    {smallSectionTabIndex === 1 && (
                      <Box>
                        {/* Features */}
                        <InputRowContainer>
                          <MultiSelect
                            labelText={'Feature(s)'}
                            labelId={'features'}
                            mandatory={false}
                            fullWidth={true}
                            helpTextTitle={dFeatureTitle}
                            helpTextBody={dFeatureBody}
                            onChange={(value) => {
                              handleSectionChange('features', value);
                              setTouchedFields((prev) => ({
                                ...prev,
                                roles: true,
                              })); // Mark as touched
                            }}
                            options={sectionFeatureOptions}
                            selectedValues={sectionFeatureOptions.filter(option => `${formSectionState.features}`.includes(option.value))}
                            disabled={readOnlySection || (locksDisabled && (formSectionState.lockedFields || []).includes('features') )}
                            onLock={handleLockedFields}
                            disabledLock={locksDisabled}
                            displayedLock={locksDisplayed}
                            isLocked={(formSectionState.lockedFields || []).includes('features')}
                          />
                        </InputRowContainer>
                        {/* Display type and Group identifier on the same line, with Group Header inline below Group identifier */}
                        <Box sx={{ display: 'flex', flexDirection: 'row', gap: 2, alignItems: 'flex-start' }}>
                          <Box sx={{ flex: 1 }}>
                            <SingleSelect
                              labelText={'Display type'}
                              labelId={'displayType'}
                              mandatory={true}
                              fullWidth={true}
                              helpTextTitle={dDisplayHelpTitle}
                              helpTextBody={dDisplayHelpBody}
                              onChange={(value) =>
                                handleSectionChange('displayType', value)
                              }
                              options={dislayTypeOptions}
                              selectedValues={dislayTypeOptions.filter(option => formSectionState.displayType === option.value)}
                              disabled={readOnlySection || (locksDisabled && (formSectionState.lockedFields || []).includes('displayType') )}
                              onLock={handleLockedFields}
                              disabledLock={locksDisabled}
                              displayedLock={locksDisplayed}
                              isLocked={(formSectionState.lockedFields || []).includes('displayType')}
                            />
                          </Box>
                          <Box sx={{ flex: 1, marginTop:0 }}>
                            <InputField
                              labelText={'Group identifier'}
                              labelId={'groupId'}
                              mandatory={false}
                              helpTextTitle={dGroupIdHelpTitle}
                              helpTextBody={dGroupIdHelpBody}
                              onChange={(value) => handleSectionChange('groupId', value, props.language, 'value')}
                              onBlur={() => {
                                setTouchedFields((prev) => ({
                                  ...prev,
                                  groupId: true,
                                }));
                              } }
                              fullWidth={true}
                              value={formSectionState.groupId || ''}
                              error={false}
                              disabled={readOnlySection || (locksDisabled && (formSectionState.lockedFields || []).includes('groupId') )}
                              onLock={handleLockedFields}
                              disabledLock={locksDisabled}
                              displayedLock={locksDisplayed}
                              isLocked={(formSectionState.lockedFields || []).includes('groupId')}
                            />
                            {/* Group Header checkbox inline below Group identifier, styled to match Display type label */}
                            {formSectionState?.groupId && formSectionState.groupId.length > 0 && (
                              <Box sx={{ mt: 0 }}>
                                <FormControlLabel
                                  control={
                                    <Checkbox
                                      name="group header"
                                      checked={!!formSectionState.groupHeader}
                                      onChange={(e) => {
                                        handleSectionChange('groupHeader', e.target.checked);
                                      }}
                                    />
                                  }
                                  label={<span style={{ fontFamily: 'Roboto, Helvetica, Arial, sans-serif', fontWeight: 200, fontSize: '14px', color: '#212121' }}>Group Header</span>}
                                  sx={{ ml: 0 }}
                                />
                                {groupHeaderError && (
                                  <Typography color="error" variant="caption" sx={{ ml: 1 }}>{groupHeaderError}</Typography>
                                )}
                              </Box>
                            )}
                          </Box>
                        </Box>
                        <Box sx={{ display: 'flex', flexDirection: 'row', gap: 2, alignItems: 'flex-start', mt: 1.25 }}>
                          <Box sx={{ flex: 1 }}>
                            <SingleSelect
                              labelText={'Workflow Task'}
                              labelId={'workflowTask'}
                              mandatory={false}
                              fullWidth={true}
                              helpTextTitle={'Workflow Task (optional)'}
                              helpTextBody={'Onsite workflow task/subtask'}
                              onChange={(value) => handleSectionChange('workflowTask', value)}
                              options={sectionWorkflowTaskOptions}
                              selectedValues={sectionWorkflowTaskOptions.filter(option => {
                                const legacySectionState = formSectionState as FbeSection & { workflowTaskType?: string };
                                return (formSectionState.workflowTask || legacySectionState.workflowTaskType || '') === option.value;
                              })}
                              disabled={readOnlySection || (locksDisabled && ((formSectionState.lockedFields || []).includes('workflowTask') || (formSectionState.lockedFields || []).includes('workflowTaskType')) )}
                              onLock={handleLockedFields}
                              disabledLock={locksDisabled}
                              displayedLock={locksDisplayed}
                              isLocked={(formSectionState.lockedFields || []).includes('workflowTask') || (formSectionState.lockedFields || []).includes('workflowTaskType')}
                            />
                          </Box>
                          <Box sx={{ flex: 1 }} />
                        </Box>
                      </Box>
                    )}

                    {smallSectionTabIndex === 2 && (
                      <Box>
                        {/* Dependency */}

                        <InputRowContainer>
                          <Box><FieldLock
                            onChange={function (locked: boolean): void {
                                handleLockedFields('workflowDependency',locked);
                            } }
                            labelId={'workflowDependency'}
                            disabled={locksDisabled}
                        displayed={locksDisplayed}
                            value={(formSectionState.lockedFields || []).includes('workflowDependency')}/>
                          {'Workflow dependencies'}</Box>
                        </InputRowContainer>
                        <Box>
                          <InputRowContainer>
                          <MultiSelect
                            labelText={'Workflow dependency state(s)'}
                            labelId={'workflowDependency'}
                            mandatory={false}
                            fullWidth={true}
                            helpTextTitle={dDependencyWorkflowTitle}
                            helpTextBody={dDependencyWorkflowBody}
                            onChange={(value) => {
                              handleSectionChange('workflowDependency', value);
                            }}
                            options={activeWorkflowStatusOptions}
                            selectedValues={activeWorkflowStatusOptions.filter(option => `${formSectionState.workflowDependency}`.includes(option.value))}
                            disabled={readOnlySection || (locksDisabled && (formSectionState.lockedFields || []).includes('workflowDependency') )}
                            onLock={handleLockedFields}
                            disabledLock={locksDisabled}
                            displayedLock={false}
                            isLocked={(formSectionState.lockedFields || []).includes('workflowDependency')}
                            showWorkflowInfo={true}
                          />
                          </InputRowContainer>
                        </Box>

                        <InputRowContainer>
                        <Box>
                          <FieldLock
                            onChange={function (locked: boolean): void {
                                handleLockedFields('externalDependency',locked);
                            } }
                            labelId={'externalDependency'}
                            disabled={locksDisabled}
                            displayed={locksDisplayed}
                            value={(formSectionState.lockedFields || []).includes('externalDependency')}/>
                          {'External dependencies'}</Box>
                         </InputRowContainer>
                        { formSectionState.externalDependency?.map(
                          (data, externalIndex) => (
                            <Box key={externalIndex} >
                              <InputRowContainer>
                                <SingleSelect
                                  labelText={'Logical operator'}
                                  labelId={'logicalOperator'}
                                  mandatory={true}
                                  fullWidth={true}
                                  helpTextTitle={sdLogicalOperatorTitle}
                                  helpTextBody={sdLogicalOperatorBody}
                                  onChange={(value) =>
                                    handleSectionChange('externalDependency', value, props.language,'logicalOperator', externalIndex)
                                  }
                                  options={logicalOperatorOptions}
                                  selectedValues={logicalOperatorOptions.filter(option => data.logicalOperator === option.value)}
                                  disabled={readOnlySection || (locksDisabled && (formSectionState.lockedFields || []).includes('externalDependency') )}
                                  onLock={handleLockedFields}
                                  disabledLock={locksDisabled}
                                  displayedLock={false}
                                  isLocked={(formSectionState.lockedFields || []).includes('externalDependency')}
                                />
                                <SingleSelect
                                  labelText={'Variable'}
                                  labelId={'variable'}
                                  mandatory={true}
                                  fullWidth={true}
                                  helpTextTitle={sdVariableTitle}
                                  helpTextBody={sdVariableBody}
                                  onChange={(value) =>
                                    handleSectionChange('externalDependency', value, props.language,'variable', externalIndex)
                                  }
                                  options={generateExternalDataToOptions(props.form?.externalData || [], 'externalVariable')}
                                  selectedValues={generateExternalDataToOptions(props.form?.externalData || [], 'externalVariable').filter(option => data.variable === option.value)}
                                  disabled={readOnlySection || (locksDisabled && (formSectionState.lockedFields || []).includes('externalDependency') )}
                                  onLock={handleLockedFields}
                                  disabledLock={locksDisabled}
                                  displayedLock={false}
                                  isLocked={(formSectionState.lockedFields || []).includes('externalDependency')}
                                />
                              </InputRowContainer>
                              <InputRowContainer>
                              <SingleSelect
                                  labelText={'Operator'}
                                  labelId={'operator'}
                                  mandatory={true}
                                  fullWidth={true}
                                  helpTextTitle={sdOperatorTitle}
                                  helpTextBody={sdOperatorBody}
                                  onChange={(value) =>
                                    handleSectionChange('externalDependency', value, props.language,'operator', externalIndex)
                                  }
                                  options={operatorOptions}
                                  selectedValues={operatorOptions.filter(option => data.operator === option.value)}
                                  disabled={readOnlySection || (locksDisabled && (formSectionState.lockedFields || []).includes('externalDependency') )}
                                  onLock={handleLockedFields}
                                  disabledLock={locksDisabled}
                                  displayedLock={false}
                                  isLocked={(formSectionState.lockedFields || []).includes('externalDependency')}
                                />
                                <InputField
                                  labelText={'Compare value'}
                                  labelId={'compareValue'}
                                  mandatory={false}
                                  helpTextTitle={sdCompareValueTitle}
                                  helpTextBody={sdCompareValueBody}
                                  onChange={(value) =>
                                    handleSectionChange('externalDependency', value, props.language,'compareValue', externalIndex)
                                  }
                                  onBlur={() =>
                                    setTouchedFields((prev) => ({
                                      ...prev,
                                      externalDependency: true,
                                    }))
                                  }
                                  fullWidth={true}
                                  value={data.compareValue || ''}
                                  error={false}
                                  helperText={''}
                                  disabled={readOnlySection || (locksDisabled && (formSectionState.lockedFields || []).includes('externalDependency') )}
                                  onLock={handleLockedFields}
                                  disabledLock={locksDisabled}
                                  displayedLock={false}
                                  isLocked={(formSectionState.lockedFields || []).includes('externalDependency')}
                                />
                              </InputRowContainer>
                              <InputRowContainer>
                                <SingleSelect
                                  labelText={'Compare variable'}
                                  labelId={'compareVariable'}
                                  mandatory={false}
                                  fullWidth={true}
                                  helpTextTitle={sdCompareVariableTitle}
                                  helpTextBody={sdCompareVariableBody}
                                  onChange={(value) =>
                                    handleSectionChange('externalDependency', value, props.language,'compareVariable', externalIndex)
                                  }
                                  options={generateExternalDataToOptions(props.form?.externalData || [], 'externalVariable')}
                                  selectedValues={generateExternalDataToOptions(props.form?.externalData || [], 'externalVariable').filter(option => data.compareVariable === option.value)}
                                  disabled={readOnlySection || (locksDisabled && (formSectionState.lockedFields || []).includes('externalDependency') )}
                                  onLock={handleLockedFields}
                                  disabledLock={locksDisabled}
                                  displayedLock={false}
                                  isLocked={(formSectionState.lockedFields || []).includes('externalDependency')}
                                />
                              </InputRowContainer>
                              <InputRowContainer>
                                <LineAction
                                  action={LineActionType.REMOVE}
                                  onClick={() =>
                                    handleRemoveDependency(
                                      true,
                                      externalIndex,
                                    )
                                  }
                                  actionText={'Remove this dependency'}
                                  helpTextTitle={sdRemoveDependencyTitle}
                                  helpTextBody={sdRemoveDependencyBody}
                                  labelId={`${externalIndex}Remove this dependency`}
                                  disabled={readOnlySection}
                                />
                                </InputRowContainer>
                            </Box>
                        ))}
                        <InputRowContainer>
                          <LineAction
                            action={LineActionType.ADD}
                            onClick={() =>
                              handleAddDependency(true)
                            }
                            actionText={'Add new dependency'}
                            helpTextTitle={sdAddDependencyTitle}
                            helpTextBody={sdAddDependencyBody}
                            labelId={'Add new external dependency'}
                            disabled={readOnlySection}
                          />
                        </InputRowContainer>

                        <InputRowContainer>
                          <Box>
                          <FieldLock
                            onChange={function (locked: boolean): void {
                                handleLockedFields('internalDependency',locked);
                            } }
                            labelId={'internalDependency'}
                            disabled={locksDisabled}
                            displayed={locksDisplayed}
                            value={(formSectionState.lockedFields || []).includes('internalDependency')}/>
                          {'Internal dependencies'}</Box>
                         </InputRowContainer>
                        { formSectionState.internalDependency?.map(
                          (data, internalIndex) => (
                            <Box key={internalIndex} >
                              <InputRowContainer>
                                <SingleSelect
                                  labelText={'Logical operator'}
                                  labelId={'logicalOperator'}
                                  mandatory={true}
                                  fullWidth={true}
                                  helpTextTitle={sdLogicalOperatorTitle}
                                  helpTextBody={sdLogicalOperatorBody}
                                  onChange={(value) =>
                                    handleSectionChange('internalDependency', value, props.language,'logicalOperator', internalIndex)
                                  }
                                  options={logicalOperatorOptions}
                                  selectedValues={logicalOperatorOptions.filter(option => data.logicalOperator === option.value)}
                                  disabled={readOnlySection || (locksDisabled && (formSectionState.lockedFields || []).includes('internalDependency') )}
                                  onLock={handleLockedFields}
                                  disabledLock={locksDisabled}
                                  displayedLock={false}
                                  isLocked={(formSectionState.lockedFields || []).includes('internalDependency')}
                                />
                                <SingleSelect
                                  labelText={'Variable'}
                                  labelId={'variable'}
                                  mandatory={true}
                                  fullWidth={true}
                                  helpTextTitle={sdVariableTitle}
                                  helpTextBody={sdVariableBody}
                                  onChange={(value) =>
                                    handleSectionChange('internalDependency', value, props.language,'variable', internalIndex)
                                  }
                                  options={generatInternalVariableToOptions(props.formSections || [], 'internalVariable')}
                                  selectedValues={generatInternalVariableToOptions(props.formSections || [], 'internalVariable').filter(option => data.variable === option.value)}
                                  disabled={readOnlySection || (locksDisabled && (formSectionState.lockedFields || []).includes('internalDependency') )}
                                  onLock={handleLockedFields}
                                  disabledLock={locksDisabled}
                                  displayedLock={false}
                                  isLocked={(formSectionState.lockedFields || []).includes('internalDependency')}
                                />
                              </InputRowContainer>
                              <InputRowContainer>
                              <SingleSelect
                                  labelText={'Operator'}
                                  labelId={'operator'}
                                  mandatory={true}
                                  fullWidth={true}
                                  helpTextTitle={sdOperatorTitle}
                                  helpTextBody={sdOperatorBody}
                                  onChange={(value) =>
                                    handleSectionChange('internalDependency', value, props.language,'operator', internalIndex)
                                  }
                                  options={operatorOptions}
                                  selectedValues={operatorOptions.filter(option => data.operator === option.value)}
                                  disabled={readOnlySection || (locksDisabled && (formSectionState.lockedFields || []).includes('internalDependency') )}
                                  onLock={handleLockedFields}
                                  disabledLock={locksDisabled}
                                  displayedLock={false}
                                  isLocked={(formSectionState.lockedFields || []).includes('internalDependency')}
                                />
                                <InputField
                                  labelText={'Compare value'}
                                  labelId={'compareValue'}
                                  mandatory={false}
                                  helpTextTitle={sdCompareValueTitle}
                                  helpTextBody={sdCompareValueBody}
                                  onChange={(value) =>
                                    handleSectionChange('internalDependency', value, props.language,'compareValue', internalIndex)
                                  }
                                  onBlur={() =>
                                    setTouchedFields((prev) => ({
                                      ...prev,
                                      internalDependency: true,
                                    }))
                                  }
                                  fullWidth={true}
                                  value={data.compareValue || ''}
                                  error={false}
                                  helperText={''}
                                  disabled={readOnlySection || (locksDisabled && (formSectionState.lockedFields || []).includes('internalDependency') )}
                                  onLock={handleLockedFields}
                                  disabledLock={locksDisabled}
                                  displayedLock={false}
                                  isLocked={(formSectionState.lockedFields || []).includes('internalDependency')}
                                />
                              </InputRowContainer>
                              <InputRowContainer>
                                <SingleSelect
                                  labelText={'Compare variable'}
                                  labelId={'compareVariable'}
                                  mandatory={false}
                                  fullWidth={true}
                                  helpTextTitle={sdCompareVariableTitle}
                                  helpTextBody={sdCompareVariableBody}
                                  onChange={(value) =>
                                    handleSectionChange('internalDependency', value, props.language,'compareVariable', internalIndex)
                                  }
                                  options={generatInternalVariableToOptions(props.formSections || [], 'internalVariable')}
                                  selectedValues={generatInternalVariableToOptions(props.formSections || [], 'internalVariable').filter(option => data.compareVariable === option.value)}
                                  disabled={readOnlySection || (locksDisabled && (formSectionState.lockedFields || []).includes('internalDependency') )}
                                  onLock={handleLockedFields}
                                  disabledLock={locksDisabled}
                                  displayedLock={false}
                                  isLocked={(formSectionState.lockedFields || []).includes('internalDependency')}
                                />
                              </InputRowContainer>
                              <InputRowContainer>
                                <LineAction
                                  action={LineActionType.REMOVE}
                                  onClick={() =>
                                    handleRemoveDependency(
                                      false,
                                      internalIndex,
                                    )
                                  }
                                  actionText={'Remove this dependency'}
                                  helpTextTitle={sdRemoveDependencyTitle}
                                  helpTextBody={sdRemoveDependencyBody}
                                  labelId={`${internalIndex}Remove this dependency`}
                                  disabled={readOnlySection}
                                />
                                </InputRowContainer>
                            </Box>
                        ))}
                        <InputRowContainer>
                          <LineAction
                            action={LineActionType.ADD}
                            onClick={() =>
                              handleAddDependency(false)
                            }
                            actionText={'Add new dependency'}
                            helpTextTitle={sdAddDependencyTitle}
                            helpTextBody={sdAddDependencyBody}
                            labelId={'Add new internal dependency'}
                            disabled={readOnlySection}
                          />
                        </InputRowContainer>

                        </Box>
                      )}


                    {smallSectionTabIndex === 3 && (
                      <Box>
                        { /* Meta data */ }
                        <FieldLock
                            onChange={function (locked: boolean): void {
                                handleLockedFields('mergeMetaData',locked);
                            } }
                            labelId={'mergeMetaData'}
                            disabled={locksDisabled}
                        displayed={locksDisplayed}
                            value={(formSectionState.lockedFields || []).includes('mergeMetaData')}/>
                          Meta data
                        <Box>
                          <InputRowContainer>
                            <MultiSelect
                              labelText={'Role(s)'}
                              labelId={'roles'}
                              mandatory={true}
                              fullWidth={true}
                              helpTextTitle={mdRolesTitle}
                              helpTextBody={mdRolesBody}
                              onChange={(value) => {
                              handleSectionChange('mergeMetaData', {
                                questionSetId: formSectionState.mergeMetaData?.questionSetId || '',
                                parentSetId: formSectionState.mergeMetaData?.parentSetId || '',
                                tacoRole: value,
                                description: formSectionState.mergeMetaData?.description || [],
                                tag: formSectionState.mergeMetaData?.tag || '',
                               } as FbeSection['mergeMetaData']);
                              setTouchedFields((prev) => ({
                                ...prev,
                                tacoRole: true,
                                }));
                              }}
                              options={roleOptions}
                              selectedValues={roleOptions.filter(option => `${formSectionState.mergeMetaData?.tacoRole || []}`.includes(option.value))}
                              disabled={readOnlySection || (locksDisabled && (formSectionState.lockedFields || []).includes('roles') )}
                              onLock={handleLockedFields}
                              disabledLock={locksDisabled}
                              displayedLock={false}
                              isLocked={(formSectionState.lockedFields || []).includes('roles')}
                            />
                          </InputRowContainer>
                          <InputRowContainer>
                          <InputField
                            labelText={'Navigation text'}
                            labelId={'description'}
                            mandatory={false}
                            helpTextTitle={mdDescriptionHelpTitle}
                            helpTextBody={mdDescriptionHelpBody}
                            onChange={(value) =>
                                  handleSectionChange('mergeMetaData', {
                                    ...formSectionState.mergeMetaData,
                                    description: getMergeMetaDataDescription(props.language, value, formSectionState.mergeMetaData?.description),
                                    questionSetId:
                                      formSectionState.mergeMetaData
                                        ?.questionSetId || '', // Provide default value
                                    parentSetId:
                                      formSectionState.mergeMetaData?.parentSetId ||
                                      '', // Provide default value
                                    tacoRole:
                                      formSectionState.mergeMetaData?.tacoRole ||
                                      [], // Provide default value
                                    tag: formSectionState.mergeMetaData?.tag || '', // Provide default value
                                  } as FbeSection['mergeMetaData'])
                            }
                            onBlur={() =>
                                  setTouchedFields((prev) => ({
                                    ...prev,
                                    mergeMetaData: true,
                                  }))
                            }
                            fullWidth={true}
                            rows={4}
                            value={
                                  formSectionState.mergeMetaData?.description[0]
                                    ?.text || ''
                            }
                            error={false}
                            helperText={''}
                            disabled={readOnlySection || (locksDisabled && (formSectionState.lockedFields || []).includes('mergeMetaData') )}
                            onLock={handleLockedFields}
                            disabledLock={locksDisabled}
                    displayedLock={false}
                            isLocked={(formSectionState.lockedFields || []).includes('mergeMetaData')}
                          />
                          </InputRowContainer>
                          <InputRowContainer>
                           <Box sx={{display:'flex', gap:2, width: '100%'}}>
                           <Box sx={{flex: 1}}>
                          <InputField
                            labelText={'Question set ID'}
                            labelId={'questionSetID'}
                            mandatory={true}
                            helpTextTitle={mdQuestionSetIDHelpTitle}
                            helpTextBody={mdQuestionSetIDHelpBody}
                              onChange={(value) =>
                              handleSectionChange('mergeMetaData', {
                                ...formSectionState.mergeMetaData,
                                questionSetId: value,
                                tag: formSectionState.mergeMetaData?.tag || '', // Default value
                                parentSetId:
                                  formSectionState.mergeMetaData?.parentSetId ||
                                  '', // Default value
                                tacoRole:
                                  formSectionState.mergeMetaData?.tacoRole ||
                                  [], // Default value
                                description: formSectionState.mergeMetaData
                                  ?.description || [
                                  { language: props.language, text: '' },
                                ], // Default value
                              } as FbeSection['mergeMetaData'])
                            }
                            onBlur={() =>
                                  setTouchedFields((prev) => ({
                                    ...prev,
                                    mergeMetaData: true,
                                  }))
                            }
                            fullWidth={true}
                            value={
                              formSectionState.mergeMetaData?.questionSetId || ''
                            }
                            error={false}
                            helperText={''}
                            disabled={readOnlySection || (locksDisabled && (formSectionState.lockedFields || []).includes('mergeMetaData') )}
                            onLock={handleLockedFields}
                            disabledLock={locksDisabled}
                    displayedLock={false}
                            isLocked={(formSectionState.lockedFields || []).includes('mergeMetaData')}
                          />
                          </Box>
                          <Box sx={{flex: 1, display: 'flex', alignItems: 'center', gap: 1}}>
                            <InputField
                              labelText={'Parent set ID'}
                              labelId={'parentSetID'}
                              mandatory={true}
                              helpTextTitle={mdParentSetIDHelpTitle}
                              helpTextBody={mdParentSetIDHelpBody}
                              onChange={(value) =>
                                handleSectionChange('mergeMetaData', {
                                  ...formSectionState.mergeMetaData,
                                  parentSetId: value,
                                  tag: formSectionState.mergeMetaData?.tag || '', // Default value
                                  questionSetId:
                                    formSectionState.mergeMetaData
                                      ?.questionSetId || '', // Default value
                                  tacoRole:
                                    formSectionState.mergeMetaData?.tacoRole ||
                                    [], // Default value
                                  description: formSectionState.mergeMetaData
                                    ?.description || [
                                    { language: props.language, text: '' },
                                  ], // Default value
                                } as FbeSection['mergeMetaData'])
                              }
                              onBlur={() =>
                                    setTouchedFields((prev) => ({
                                      ...prev,
                                      mergeMetaData: true,
                                    }))
                              }
                              fullWidth={true}
                              value={
                                formSectionState.mergeMetaData?.parentSetId || ''
                              }
                              error={false}
                              helperText={''}
                              disabled={readOnlySection || (locksDisabled && (formSectionState.lockedFields || []).includes('mergeMetaData') )}
                              onLock={handleLockedFields}
                              disabledLock={locksDisabled}
                      displayedLock={false}
                              isLocked={(formSectionState.lockedFields || []).includes('mergeMetaData')}
                            />
                            <Tooltip title="Show parent set ranges">
                              <span>
                                <IconButton
                                  size="small"
                                  onClick={handleOpenParentSetList}
                                  disabled={readOnlySection || (locksDisabled && (formSectionState.lockedFields || []).includes('mergeMetaData') )}
                                  aria-label="parent-set-range"
                                  sx={{ ml: 0.5 }}
                                >
                                  <ListAlt fontSize="small" />
                                </IconButton>
                              </span>
                            </Tooltip>
                          </Box>
                          </Box>
                          <Popover
                            anchorEl={parentAnchorEl}
                            open={Boolean(parentAnchorEl)}
                            onClose={handleCloseParentSetList}
                            anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
                            transformOrigin={{ vertical: 'top', horizontal: 'left' }}
                            PaperProps={{ sx: { p: 0, borderRadius: 2, boxShadow: 3 } }}
                          >
                            <List sx={{ minWidth: 320, maxHeight: 340, overflow: 'auto', userSelect: 'none', p:0 }}>
                              {((props.allForms && props.allForms.length > 0) ? props.allForms : (props.form ? [props.form] : [])).map((f, idx) => {
                                const title = f.name && f.name.length > 0 ? f.name[0].text : (f.formId || f.pk || `form-${idx}`);
                                const range = f.questionSetIdRange || 'No range available';
                                // If the form has no range, render a disabled row (not selectable)
                                if (!f.questionSetIdRange) {
                                  return (
                                    <ListItem
                                      key={`${f.pk || idx}-${f.sk || idx}`}
                                      disablePadding
                                      sx={{ px: 1.5, py: 0.75, color: 'text.disabled' }}
                                    >
                                      <ListItemText
                                        primary={`${title} - ${range}`}
                                        primaryTypographyProps={{ fontSize: 14 }}
                                        sx={{ userSelect: 'none', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}
                                      />
                                    </ListItem>
                                  );
                                }

                                return (
                                  <ListItem
                                    key={`${f.pk || idx}-${f.sk || idx}`}
                                    disablePadding
                                    sx={{ px: 1.5, py: 0.75, '&:hover': { backgroundColor: 'action.hover', cursor: 'pointer' } }}
                                    onClick={() => {
                                      // set the parentSetId to the selected form's range
                                      handleSectionChange('mergeMetaData', {
                                        ...formSectionState.mergeMetaData,
                                        parentSetId: f.questionSetIdRange,
                                        tag: formSectionState.mergeMetaData?.tag || '',
                                        questionSetId: formSectionState.mergeMetaData?.questionSetId || '',
                                        tacoRole: formSectionState.mergeMetaData?.tacoRole || [],
                                        description: formSectionState.mergeMetaData?.description || [{ language: props.language, text: '' }],
                                      } as FbeSection['mergeMetaData']);
                                      handleCloseParentSetList();
                                    }}
                                  >
                                    <ListItemText
                                      primary={`${title} - ${range}`}
                                      primaryTypographyProps={{ fontSize: 14, fontWeight: 500 }}
                                      sx={{ userSelect: 'none', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}
                                    />
                                  </ListItem>
                                )
                              })}
                            </List>
                          </Popover>
                          </InputRowContainer>
                          <InputRowContainer>
                          <InputField
                            labelText={'Tag'}
                            labelId={'tag'}
                            mandatory={true}
                            helpTextTitle={mdTagHelpTitle}
                            helpTextBody={mdTagHelpBody}
                              onChange={(value) =>
                              handleSectionChange('mergeMetaData', {
                                ...formSectionState.mergeMetaData,
                                tag: value,
                                questionSetId:
                                  formSectionState.mergeMetaData
                                    ?.questionSetId || '', // Default value
                                parentSetId:
                                  formSectionState.mergeMetaData?.parentSetId ||
                                  '', // Default value
                                tacoRole:
                                  formSectionState.mergeMetaData?.tacoRole ||
                                  [], // Default value
                                description: formSectionState.mergeMetaData
                                  ?.description || [
                                  { language: props.language, text: '' },
                                ], // Default value
                              } as FbeSection['mergeMetaData'])
                            }
                            onBlur={() =>
                                  setTouchedFields((prev) => ({
                                    ...prev,
                                    mergeMetaData: true,
                                  }))
                            }
                            fullWidth={true}
                            value={
                              formSectionState.mergeMetaData?.tag || ''
                            }
                            error={false}
                            helperText={''}
                            disabled={readOnlySection || (locksDisabled && (formSectionState.lockedFields || []).includes('mergeMetaData') )}
                            onLock={handleLockedFields}
                            disabledLock={locksDisabled}
                    displayedLock={false}
                            isLocked={(formSectionState.lockedFields || []).includes('mergeMetaData')}
                          />
                          </InputRowContainer>
                        </Box>
                      </Box>
                    )}

                    {smallSectionTabIndex === 4 && (
                      <Box>
                      { /* Loop */ }
                      <Box>
                        <FieldLock
                            onChange={function (locked: boolean): void {
                                handleLockedFields('loop',locked);
                            } }
                            labelId={'loop'}
                            disabled={locksDisabled}
                        displayed={locksDisplayed}
                            value={(formSectionState.lockedFields || []).includes('loop')}/>
                          Loop
                        <InputRowContainer>
                        <FBCheckbox
                          labelText={'Enable looping'}
                          labelId={'enableLooping'}
                          mandatory={true}
                          helpTextTitle={lLoopingHelpTitle}
                          helpTextBody={lLoopingHelpBody}
                          checked={isLoopingEnabled}
                          onChange={(checked) => {
                            setIsLoopingEnabled(checked);
                            if(checked && !formSectionState.loop){
                              addLoop();
                            } else if(!checked){
                              removeLoop();
                            }
                          }
                          }
                          disabled={readOnlySection || (locksDisabled && (formSectionState.lockedFields || []).includes('loop') )}
                          onLock={handleLockedFields}
                          disabledLock={locksDisabled}
                          displayedLock={false}
                          isLocked={(formSectionState.lockedFields || []).includes('loop')}
                        />
                              <FBRadio
                                row={true}
                                labelId={'sortOrder'}
                                value={
                                  formSectionState.loop?.ascending === true
                                    ? 'ascending'
                                    : 'descending'
                                }
                                onChange={(value) =>
                                  updateLoop({ ascending: value === 'ascending' })
                                }
                                labelText={'Sort order'}
                                mandatory={true}
                                helpTextTitle={lOrderHelpTitle}
                                helpTextBody={lOrderHelpBody}
                                valueList={[
                                  {
                                    helpTitle:`{${lOrderHelpTitle}`,
                                    helpBody:`{${lOrderHelpBody}`,
                                    value: 'ascending',
                                    label:'Ascending',
                                    mandatory: true
                                  },
                                  {
                                    helpTitle:`{${lOrderHelpTitle}`,
                                    helpBody:`{${lOrderHelpBody}`,
                                    value: 'descending',
                                    label:'Descending',
                                    mandatory: true
                                  },
                                ]}
                                disabled={!isLoopingEnabled || readOnlySection || (locksDisabled && (formSectionState.lockedFields || []).includes('loop') )}
                                onLock={handleLockedFields}
                                disabledLock={locksDisabled}
                                displayedLock={false}
                                isLocked={(formSectionState.lockedFields || []).includes('loop')}
                              />
                        </InputRowContainer>
                        <InputRowContainer>
                          <SingleSelect
                            labelText={'Order internal variable'}
                            labelId={'ascendingInternalVariable'}
                            mandatory={false}
                            fullWidth={true}
                            helpTextTitle={lInternalVariableOrderHelpTitle}
                            helpTextBody={lInternalVariableOrderHelpBody}
                            onChange={(value) =>
                              updateLoop({ ascendingInternalVariable: value })
                            }
                            options={generatInternalVariableToOptions(props.formSections || [], 'ascendingInternalVariable')}
                            selectedValues={generatInternalVariableToOptions(props.formSections || [], 'ascendingInternalVariable').filter(option => formSectionState.loop?.ascendingInternalVariable === option.value)}
                            disabled={!isLoopingEnabled || readOnlySection || (locksDisabled && (formSectionState.lockedFields || []).includes('loop') )}
                            onLock={handleLockedFields}
                            disabledLock={locksDisabled}
                    displayedLock={false}
                            isLocked={(formSectionState.lockedFields || []).includes('loop')}
                          />
                          <InputField
                            labelText={'Order internal value'}
                            labelId={'ascendingInternalValue'}
                            mandatory={false}
                            helpTextTitle={lInternalValueOrderHelpTitle}
                            helpTextBody={lInternalValueOrderHelpBody}
                            onBlur={(value) => void value
                            }
                            onChange={(value) =>
                              updateLoop({ ascendingInternalValue: value })
                            }
                            fullWidth={true}
                            value={formSectionState.loop?.ascendingInternalValue || ''}
                            error={false}
                            helperText={''}
                            disabled={!isLoopingEnabled || readOnlySection || (locksDisabled && (formSectionState.lockedFields || []).includes('loop') )}
                            onLock={handleLockedFields}
                            disabledLock={locksDisabled}
                    displayedLock={false}
                            isLocked={(formSectionState.lockedFields || []).includes('loop')}
                          />
                        </InputRowContainer>

                        <InputRowContainer>
                        <InputField
                          labelText={'Name'}
                          labelId={'loopName'}
                          mandatory={true}
                          helpTextTitle={lNameHelpTitle}
                          helpTextBody={lNameHelpBody}
                          onBlur={(value) => void value
                          }
                          onChange={(value) =>
                            updateLoop({
                              name: formSectionState.loop?.name
                                ? formSectionState.loop.name.map((entry) =>
                                    entry.language === props.language
                                      ? { ...entry, text: value }
                                      : entry
                                  )
                                : [{ language: props.language, text:value }],
                            })
                          }
                          fullWidth={true}
                          value={formSectionState.loop?.name?.[0]?.text || ''}
                          error={false}
                          helperText={''}
                          disabled={!isLoopingEnabled || readOnlySection || (locksDisabled && (formSectionState.lockedFields || []).includes('loop') )}
                          onLock={handleLockedFields}
                          disabledLock={locksDisabled}
                    displayedLock={false}
                          isLocked={(formSectionState.lockedFields || []).includes('loop')}
                        />
                        <InputField
                          labelText={'Total'}
                          labelId={'loopTotal'}
                          mandatory={true}
                          helpTextTitle={lTotalHelpTitle}
                          helpTextBody={lTotalHelpBody}
                          onBlur={(value) => void value
                          }
                          onChange={(value) =>
                            updateLoop({ total: Number(value) })
                          }
                          fullWidth={true}
                          value={formSectionState.loop?.total ?? 0}
                          error={false}
                          helperText={''}
                          disabled={!isLoopingEnabled || readOnlySection || (locksDisabled && (formSectionState.lockedFields || []).includes('loop') )}
                          onLock={handleLockedFields}
                          disabledLock={locksDisabled}
                    displayedLock={false}
                          isLocked={(formSectionState.lockedFields || []).includes('loop')}
                        />
                        </InputRowContainer>
                        <InputRowContainer>
                          <MultiSelect
                            labelText={'External Variable'}
                            labelId={'loopExternalVariable'}
                            mandatory={false}
                            fullWidth={true}
                            helpTextTitle={lVariableHelpTitle}
                            helpTextBody={lVariableHelpBody}
                            onChange={handleExternalVariableChange}
                            options={generateExternalDataToOptions(props.form?.externalData || [], 'loopVariables')}
                            selectedValues={generateExternalDataToOptions(props.form?.externalData || [], 'loopVariables').filter(option =>
                              (formSectionState.loop?.externalVariable || '').split(';').includes(option.value)
                            )}
                            disabled={!isLoopingEnabled || readOnlySection || (locksDisabled && (formSectionState.lockedFields || []).includes('loop') )}
                            onLock={handleLockedFields}
                            disabledLock={locksDisabled}
                            displayedLock={false}
                            isLocked={(formSectionState.lockedFields || []).includes('loop')}
                          />
                        </InputRowContainer>
                        {getLoopCharacteristics().map((characteristic, index) => (
                          <React.Fragment key={`loop-variable-${index}`}>
                            <InputRowContainer>
                              <SingleSelect
                                labelText={`External Variable ${index + 2}`}
                                labelId={`loopExternalVariable-${index + 1}`}
                                mandatory={false}
                                fullWidth={true}
                                helpTextTitle={lVariableHelpTitle}
                                helpTextBody={lVariableHelpBody}
                                onChange={(value) => handleLoopCharacteristicChange(index, value)}
                                options={generateExternalDataToOptions(props.form?.externalData || [], 'loopVariables').map(option => ({
                                  ...option,
                                  disabled: option.value !== characteristic && getLoopCharacteristics().includes(option.value),
                                }))}
                                selectedValues={generateExternalDataToOptions(props.form?.externalData || [], 'loopVariables').filter(option => characteristic === option.value)}
                                disabled={!isLoopingEnabled || readOnlySection || (locksDisabled && (formSectionState.lockedFields || []).includes('loop') )}
                                onLock={handleLockedFields}
                                disabledLock={locksDisabled}
                                displayedLock={false}
                                isLocked={(formSectionState.lockedFields || []).includes('loop')}
                              />
                            </InputRowContainer>
                            <InputRowContainer>
                              <LineAction
                                action={LineActionType.REMOVE}
                                onClick={() => removeLoopCharacteristic(index)}
                                actionText={'Remove external variable'}
                                helpTextTitle={lVariableHelpTitle}
                                helpTextBody={lVariableHelpBody}
                                labelId={`Remove loop external variable ${index + 2}`}
                                disabled={!isLoopingEnabled || readOnlySection}
                              />
                            </InputRowContainer>
                          </React.Fragment>
                        ))}
                        <InputRowContainer>
                          <LineAction
                            action={LineActionType.ADD}
                            onClick={addLoopCharacteristic}
                            actionText={'Add external variable'}
                            helpTextTitle={lVariableHelpTitle}
                            helpTextBody={lVariableHelpBody}
                            labelId={'Add loop external variable'}
                            disabled={!isLoopingEnabled || readOnlySection || (locksDisabled && (formSectionState.lockedFields || []).includes('loop') )}
                          />
                        </InputRowContainer>


                      </Box>
                      </Box>
                    )}

                    {smallSectionTabIndex === 5 && (
                    <Box>
                    {/* references */}
                    <InputRowContainer>
                        <ReferenceImage
                        readOnly={readOnlySection}
                        selectedImages={referenceImageList}
                        onAddImage={handleOnAddImage}
                        onRemoveImage={handleOnRemoveImage}

                        disabled={readOnlySection || (locksDisabled && (formSectionState.lockedFields || []).includes('references') )}
                        onLock={handleLockedFields}
                        disabledLock={locksDisabled}
                        displayedLock={locksDisplayed}
                        isLocked={(formSectionState.lockedFields || []).includes('references')}

                      />
                      </InputRowContainer>
                    </Box>
                    )}

                </EditContainer>
                </Box>
                <Box
                  mt={3}
                  display="flex"
                  flexWrap="wrap"
                  justifyContent="flex-end"
                  sx={{ gap: '16px' }}
                >
                  <Button
                    variant="outlined"
                    sx={{ borderRadius: '24px', padding: '6px 50px' }}
                    onClick={() => {
                      history('/');
                    }}
                  >
                    Cancel
                  </Button>
                  <Button
                    variant="contained"
                    color="primary"
                    sx={{ borderRadius: '24px', padding: '6px 50px' }}
                    onClick={() => {
                      window.scrollTo({ top: 0, behavior: 'smooth' });
                      props.onSubmitAllSections();
                    }}
                    disabled={ !props.form ||
                      !props.form.pk ||
                      props.form.pk === '' ||
                      !formSectionValid ||
                      !changesMade}
                  >
                    Save
                  </Button>
                </Box>
              </>
            )}
          </Box>
          <Box mt={3} sx={{ paddingLeft: '24px', paddingRight: '24px' }}>
            {tabIndex === 1 && (
              <SectionTranslationsTab
                section={formSectionState}
                originalSection={props.originalSection || props.section}
                defaultLanguage={props.form?.defaultLanguage}
                supportedLanguages={props.supportedLanguages}
                frontline={props.frontline}
                readOnly={props.translationsReadOnly ?? props.readOnly}
                selectedLanguage={previewLanguage}
                onSave={(updatedSection) => {
                  props.onUpdateSection(updatedSection);
                  const updatedSections = [...props.formSections];
                  updatedSections[props.sectionNumber] = updatedSection;
                  props.onSubmitAllSections(updatedSections);
                }}
              />
            )}
          </Box>
          <Box mt={3} sx={{ paddingLeft: '24px', paddingRight: '24px' }}>
            {tabIndex === 2 && (
              <>
                <FormPreview language={previewLanguage} form={props.form} sections={[props.section]} parentForm={props.parentForm} parentSections={parentSection ? [parentSection] : []}/>
              </>
            )}
          </Box>
        </Grid>
    </>
  );
}